import React from 'react';
import { Form } from 'react-bootstrap';

type FormControlProps = React.ComponentProps<typeof Form.Control>;
type FormCheckProps = React.ComponentProps<typeof Form.Check>;

interface BaseInputProps extends Omit<FormControlProps, 'type'> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  controlId?: string;
  wrap?: boolean;
}

interface BaseCheckProps extends Omit<FormCheckProps, 'type'> {
  helperText?: React.ReactNode;
  wrap?: boolean;
}

const renderGroupedControl = (
  control: React.ReactElement,
  {
    wrap,
    label,
    helperText,
    controlId,
  }: Pick<BaseInputProps, 'wrap' | 'label' | 'helperText' | 'controlId'>,
): React.ReactElement => {
  if (wrap === false) {
    return control;
  }

  return (
    <Form.Group controlId={controlId} className="mb-3">
      {label && <Form.Label>{label}</Form.Label>}
      {control}
      {helperText && <Form.Text>{helperText}</Form.Text>}
    </Form.Group>
  );
};

const createInputComponent = (type: React.HTMLInputTypeAttribute) => {
  const Component: React.FC<BaseInputProps> = ({
    label,
    helperText,
    controlId,
    wrap,
    ...rest
  }) => {
    const control = <Form.Control type={type} {...rest} />;
    return renderGroupedControl(control, { wrap, label, helperText, controlId });
  };

  Component.displayName = `Input(${type})`;
  return Component;
};

const createCheckComponent = (type: 'checkbox' | 'radio') => {
  const Component: React.FC<BaseCheckProps> = ({
    helperText,
    wrap,
    ...rest
  }) => {
    const control = <Form.Check type={type} {...rest} />;

    if (wrap === false) {
      return control;
    }

    return (
      <Form.Group className="mb-3">
        {control}
        {helperText && <Form.Text>{helperText}</Form.Text>}
      </Form.Group>
    );
  };

  Component.displayName = `Check(${type})`;
  return Component;
};

export type TextInputProps = BaseInputProps;
export const TextInput = createInputComponent('text');

export type EmailInputProps = BaseInputProps;
export const EmailInput = createInputComponent('email');

export type PasswordInputProps = BaseInputProps;
export const PasswordInput = createInputComponent('password');

export type NumberInputProps = BaseInputProps;
export const NumberInput = createInputComponent('number');

export type TelInputProps = BaseInputProps;
export const TelInput = createInputComponent('tel');

export type UrlInputProps = BaseInputProps;
export const UrlInput = createInputComponent('url');

export type SearchInputProps = BaseInputProps;
export const SearchInput = createInputComponent('search');

export type DateInputProps = BaseInputProps;
export const DateInput = createInputComponent('date');

export type TimeInputProps = BaseInputProps;
export const TimeInput = createInputComponent('time');

export type DateTimeLocalInputProps = BaseInputProps;
export const DateTimeLocalInput = createInputComponent('datetime-local');

export type MonthInputProps = BaseInputProps;
export const MonthInput = createInputComponent('month');

export type WeekInputProps = BaseInputProps;
export const WeekInput = createInputComponent('week');

export type ColorInputProps = BaseInputProps;
export const ColorInput = createInputComponent('color');

export type RangeInputProps = BaseInputProps;
export const RangeInput = createInputComponent('range');

export type FileInputProps = BaseInputProps;
export const FileInput = createInputComponent('file');

export type ButtonInputProps = BaseInputProps;
export const ButtonInput = createInputComponent('button');

export type SubmitInputProps = BaseInputProps;
export const SubmitInput = createInputComponent('submit');

export type ResetInputProps = BaseInputProps;
export const ResetInput = createInputComponent('reset');

export type ImageInputProps = BaseInputProps;
export const ImageInput = createInputComponent('image');

export const HiddenInput: React.FC<Omit<BaseInputProps, 'wrap'>> = ({
  ...rest
}) => <Form.Control type="hidden" {...rest} />;

export type CheckboxInputProps = BaseCheckProps;
export const CheckboxInput = createCheckComponent('checkbox');

export type RadioInputProps = BaseCheckProps;
export const RadioInput = createCheckComponent('radio');
